// src/utils/http.js
import router from '@/router'
import { Message } from 'element-ui'

const BASE_URL = ''

/** 并发 401 时只弹一次、只跳转一次 */
let handlingUnauthorized = false

function handleUnauthorized(toastType = 'error') {
  localStorage.removeItem('token')
  localStorage.removeItem('role')
  localStorage.removeItem('status')
  localStorage.removeItem('username')
  if (handlingUnauthorized) {
    return
  }
  handlingUnauthorized = true
  if (toastType === 'warning') {
    Message.warning('登录已过期，请重新登录')
  } else {
    Message.error('登录已过期，请重新登录')
  }
  router.push('/login').finally(() => {
    // 稍后再放开，避免同页并发请求连环弹窗
    setTimeout(() => {
      handlingUnauthorized = false
    }, 1500)
  })
}

async function request(url, options = {}) {
  const token = localStorage.getItem('token')
  
  // 去掉 /api 前缀
  const cleanUrl = url.replace(/^\/api/, '')

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { token })
  }

  const response = await fetch(`${BASE_URL}${cleanUrl}`, {
    headers,
    ...options
  })

  if (response.status === 401) {
    handleUnauthorized('error')
    return { code: 401, msg: '登录已过期' }
  }

  const result = await response.json()

  if (result.code === 200 || result.code === 1) {
    return result
  } else {
    throw new Error(result.msg || `请求失败: ${result.code}`)
  }
}

/** 从接口响应中提取可展示的成功/提示文案（忽略对象类型的 data） */
export function getResponseMessage(result, fallback = '') {
  if (!result) return fallback
  const msg = result.msg ?? result.message
  if (typeof msg === 'string' && msg.trim()) return msg.trim()
  if (typeof result.data === 'string' && result.data.trim()) return result.data.trim()
  return fallback
}

export async function get(url, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = queryString ? `${url}?${queryString}` : url
  return request(fullUrl, { method: 'GET' })
}

export async function post(url, data = {}, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = queryString ? `${url}?${queryString}` : url
  return request(fullUrl, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

export async function put(url, data = {}) {
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export async function del(url) {
  return request(url, { method: 'DELETE' })
}

// 文件上传
export async function upload(file, path = '') {
  const token = localStorage.getItem('token')
  const formData = new FormData()
  formData.append('image', file)
  if (path) formData.append('path', path)

  const response = await fetch(`${BASE_URL}/common/upload`, {
    method: 'POST',
    headers: { ...(token && { token }) },
    body: formData
  })

  const result = await response.json()
  if (result.code === 200 || result.code === 1) {
    return result
  } else {
    throw new Error(result.msg || '上传失败')
  }
}

export { BASE_URL }
export { request }

/**
 * 通用文件上传(对接后端 /api/admin/file/upload 接口,使用阿里云OSS)
 * @param {File} file - 要上传的文件
 * @returns {Promise<string>} 返回文件访问URL路径(OSS完整URL)
 */
export async function uploadFile(file) {
  const token = localStorage.getItem('token')

  if (!file || !file.size) {
    throw new Error('请选择文件')
  }

  const formData = new FormData()
  formData.append('image', file)

  const headers = {}
  if (token) {
    headers.token = token
  }

  const response = await fetch(`${BASE_URL}/admin/file/upload`, {
    method: 'POST',
    headers,
    body: formData
  })

  if (response.status === 401) {
    handleUnauthorized('warning')
    throw new Error('登录已过期')
  }

  const result = await response.json()
  if (result.code === 200 || result.code === 1) {
    return result.data
  }
  throw new Error(result.msg || '上传失败')
}

/**
 * 商城图片上传（对接 /admin/shop/goods/upload-image 或 /admin/shop/category/upload-image）
 * 后端字段名为 file，返回 result.data 为访问 URL 字符串
 * @param {string} url - 上传接口路径（如 /api/admin/shop/goods/upload-image）
 * @param {File} file - 要上传的文件
 * @returns {Promise<string>} 返回图片访问 URL
 */
export async function uploadShopImage(url, file) {
  const token = localStorage.getItem('token')

  if (!file || !file.size) {
    throw new Error('请选择文件')
  }

  const formData = new FormData()
  formData.append('file', file, file.name || 'image.png')

  // 去掉 /api 前缀
  const cleanUrl = url.replace(/^\/api/, '')

  // 使用 XMLHttpRequest 确保文件正确上传（fetch + FormData 有时会有兼容性问题）
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}${cleanUrl}`)
    if (token) {
      xhr.setRequestHeader('token', token)
    }

    xhr.onload = () => {
      if (xhr.status === 401) {
        handleUnauthorized('warning')
        reject(new Error('登录已过期'))
        return
      }

      try {
        const result = JSON.parse(xhr.responseText)
        if (result.code === 200 || result.code === 1) {
          resolve(result.data)
        } else {
          reject(new Error(result.msg || '上传失败'))
        }
      } catch (e) {
        reject(new Error('解析响应失败'))
      }
    }

    xhr.onerror = () => {
      reject(new Error('网络错误，上传失败'))
    }

    xhr.send(formData)
  })
}

// 图片资源基础路径
export const IMG_BASE_URL = ''